// Избор на населено място: стъпка 1 — област, стъпка 2 — град/село от
// списъка на областта (с филтър по букви; 200–500 реда, виртуализирани).
// Плюс RegionMultiPicker — няколко области (за майсторите).

import React, { useMemo, useState } from 'react';
import { FlatList, Modal, Pressable, Text, TextInput, View } from 'react-native';
import {
  findSettlement,
  Place,
  REGIONS,
  searchSettlements,
  Settlement,
  settlementFullLabel,
  settlementLabel,
  toPlace,
} from './geo';
import { Button, COLORS, RADIUS, Select, SP } from './ui';
import { t } from './i18n';

const ROW_HEIGHT = 52;

const fieldStyle = {
  backgroundColor: COLORS.card,
  borderWidth: 1,
  borderColor: COLORS.border,
  borderRadius: RADIUS.sm,
  paddingHorizontal: 14,
  paddingVertical: 12,
  minHeight: 48,
  flexDirection: 'row' as const,
  justifyContent: 'space-between' as const,
  alignItems: 'center' as const,
};

type Value = { city: string; region?: string | null; municipality?: string | null } | null;

export function SettlementPicker({
  value,
  onChange,
  placeholder = t('Изберете населено място'),
  fixedRegion,
  clearable,
  clearLabel = t('Всички населени места'),
}: {
  value: Value;
  onChange: (next: Place | null) => void;
  placeholder?: string;
  // Зададена област (напр. филтърът на обявите) — стъпка 1 се пропуска
  fixedRegion?: string | null;
  // Разрешава „без избор“ (връща null)
  clearable?: boolean;
  clearLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [region, setRegion] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const current = value?.city ? findSettlement(value.city, value.region) : null;
  const fieldLabel = current
    ? fixedRegion
      ? settlementLabel(current)
      : settlementFullLabel(current)
    : value?.city
      ? [value.city, value.region ? `обл. ${value.region}` : null].filter(Boolean).join(', ')
      : '';

  const openModal = () => {
    setRegion(fixedRegion ?? value?.region ?? current?.p ?? null);
    setQuery('');
    setOpen(true);
  };

  const rows = useMemo(() => (region ? searchSettlements(region, query) : []), [region, query]);

  const pick = (s: Settlement) => {
    onChange(toPlace(s));
    setOpen(false);
  };

  return (
    <>
      <Pressable onPress={openModal} style={fieldStyle}>
        <Text style={{ color: fieldLabel ? COLORS.text : COLORS.muted, fontSize: 16, flex: 1 }} numberOfLines={1}>
          {fieldLabel || placeholder}
        </Text>
        <Text style={{ color: COLORS.muted, marginLeft: 8 }}>▾</Text>
      </Pressable>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable
          onPress={() => setOpen(false)}
          style={{ flex: 1, backgroundColor: 'rgba(11,27,59,0.45)', justifyContent: 'center', padding: 24 }}
        >
          <Pressable style={{ backgroundColor: COLORS.card, borderRadius: RADIUS.lg, maxHeight: '85%', padding: SP.sm }}>
            {!region ? (
              <RegionList
                selected={value?.region ?? current?.p ?? null}
                onPick={(r) => {
                  setRegion(r);
                  setQuery('');
                }}
              />
            ) : (
              <>
                <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 6, paddingBottom: 6 }}>
                  {!fixedRegion && (
                    <Pressable onPress={() => setRegion(null)} hitSlop={8} style={{ paddingRight: 10 }}>
                      <Text style={{ color: COLORS.primary, fontSize: 16, fontWeight: '600' }}>‹ {t('Области')}</Text>
                    </Pressable>
                  )}
                  <Text style={{ color: COLORS.text, fontSize: 16, fontWeight: '700', flex: 1 }} numberOfLines={1}>
                    обл. {region}
                  </Text>
                </View>
                <TextInput
                  value={query}
                  onChangeText={setQuery}
                  autoFocus
                  autoCorrect={false}
                  placeholder={t('Търсене по име…')}
                  placeholderTextColor={COLORS.muted}
                  style={{
                    backgroundColor: COLORS.bg,
                    borderWidth: 1,
                    borderColor: COLORS.border,
                    borderRadius: RADIUS.sm,
                    paddingHorizontal: 14,
                    paddingVertical: 10,
                    fontSize: 16,
                    color: COLORS.text,
                    marginBottom: SP.sm,
                  }}
                />
                <FlatList
                  data={rows}
                  keyExtractor={(s) => `${s.t}|${s.n}|${s.m}`}
                  keyboardShouldPersistTaps="handled"
                  initialNumToRender={16}
                  windowSize={7}
                  getItemLayout={(_, index) => ({ length: ROW_HEIGHT, offset: ROW_HEIGHT * index, index })}
                  ListHeaderComponent={
                    clearable && !query ? (
                      <Pressable onPress={() => { onChange(null); setOpen(false); }} style={rowStyle(!value?.city)}>
                        <Text style={{ fontSize: 16, color: COLORS.text, fontWeight: value?.city ? '400' : '700' }}>{clearLabel}</Text>
                      </Pressable>
                    ) : null
                  }
                  ListEmptyComponent={
                    <Text style={{ color: COLORS.muted, padding: 14, textAlign: 'center' }}>{t('Няма населено място с това име.')}</Text>
                  }
                  renderItem={({ item: s }) => {
                    const selected = !!current && current.n === s.n && current.m === s.m && current.t === s.t;
                    return (
                      <Pressable onPress={() => pick(s)} style={rowStyle(selected)}>
                        <Text style={{ fontSize: 16, color: selected ? COLORS.primary : COLORS.text, fontWeight: selected ? '700' : '400' }} numberOfLines={1}>
                          {s.t === 'град' ? 'гр. ' : 'с. '}
                          {s.n}
                          {s.t === 'село' ? <Text style={{ color: COLORS.muted, fontSize: 14 }}> · общ. {s.m.replace(/\s*община\s*/iu, ' ').trim()}</Text> : null}
                        </Text>
                        {selected ? <Text style={{ color: COLORS.primary, fontWeight: '700' }}>✓</Text> : null}
                      </Pressable>
                    );
                  }}
                />
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const rowStyle = (selected: boolean) => ({
  height: ROW_HEIGHT,
  paddingHorizontal: 14,
  borderRadius: RADIUS.sm,
  flexDirection: 'row' as const,
  justifyContent: 'space-between' as const,
  alignItems: 'center' as const,
  backgroundColor: selected ? COLORS.primarySoft : 'transparent',
});

// Стъпка 1: списък на областите (текущата е отбелязана)
function RegionList({ selected, onPick }: { selected: string | null; onPick: (r: string) => void }) {
  return (
    <FlatList
      data={REGIONS}
      keyExtractor={(r) => r}
      keyboardShouldPersistTaps="handled"
      ListHeaderComponent={
        <Text style={{ color: COLORS.muted, fontSize: 12, fontWeight: '700', letterSpacing: 0.6, textTransform: 'uppercase', padding: 8 }}>
          {t('Изберете област')}
        </Text>
      }
      renderItem={({ item: r }) => {
        const active = r === selected;
        return (
          <Pressable onPress={() => onPick(r)} style={rowStyle(active)}>
            <Text style={{ fontSize: 16, color: active ? COLORS.primary : COLORS.text, fontWeight: active ? '700' : '400' }}>{r}</Text>
            <Text style={{ color: COLORS.muted }}>›</Text>
          </Pressable>
        );
      }}
    />
  );
}

// Няколко области (майстор: „Области, в които работите“) — списък с отметки
export function RegionMultiPicker({
  values,
  onChange,
  placeholder = t('Изберете области…'),
}: {
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  // подреждаме избраните в официалния ред, за да е стабилен и workArea текстът
  const ordered = (next: string[]) => REGIONS.filter((r) => next.includes(r));
  return <Select multi placeholder={placeholder} options={REGIONS} values={values} onChange={(next: string[]) => onChange(ordered(next))} />;
}

// Текстът за workArea (обратна съвместимост): „Пловдив, Пазарджик“
export const joinRegions = (regions: string[]) => regions.join(', ');

// Обратно: от workArea към области (за стари профили без workRegions)
export function parseRegions(workArea: string | null | undefined): string[] {
  if (!workArea) return [];
  const parts = workArea.split(',').map((p) => p.trim());
  return REGIONS.filter((r) => parts.includes(r));
}
